import { MapContainer, Marker, Popup, TileLayer, useMap } from 'react-leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { useEffect, useState } from 'react'
import { fetchNearby, fetchPOI } from '@services/api'
import { getPOIsOffline, getSetting, getPOIContent, savePOIContent } from '@services/indexeddb'
import POIDetails from './POIDetails'

type P = { lat: number; lon: number; name: string }

const userIcon = L.divIcon({
  className: 'custom-user-marker',
  html: `<div class="bg-tertiary text-on-tertiary p-2 md:p-3 rounded-full shadow-lg border-4 border-white flex items-center justify-center">
          <span class="material-symbols-outlined text-base" style="font-variation-settings: 'FILL' 1;">my_location</span>
         </div>`,
  iconSize: [40, 40],
  iconAnchor: [20, 20]
})

const poiIcon = L.divIcon({
  className: 'custom-poi-marker',
  html: `<div class="bg-primary text-on-primary p-1.5 md:p-2 rounded-full shadow-lg flex items-center justify-center hover:scale-110 transition-transform">
          <span class="material-symbols-outlined text-sm">place</span>
         </div>`,
  iconSize: [32, 32],
  iconAnchor: [16, 32]
})

export default function MapView({ lang = 'en' }: { lang?: string }) {
  const [pos, setPos] = useState<{ lat: number; lon: number } | null>(null)
  const [nearby, setNearby] = useState<any[]>([])
  const [selected, setSelected] = useState<any>(null)
  const [open, setOpen] = useState(false)
  
  useEffect(() => {
    let stopped = false
    const readSettings = async () => {
      const [en, la, lo] = await Promise.all([getSetting('fake_location_enabled'), getSetting('fake_lat'), getSetting('fake_lon')])
      return { enabled: !!en, lat: Number(la || 0), lon: Number(lo || 0) }
    }
    const loadWith = async (lat: number, lon: number) => {
      setPos({ lat, lon })
      try {
        let list = navigator.onLine ? (await fetchNearby(lat, lon, 50000, 'en', 10)).items : await getPOIsOffline(lat, lon, 50000)
        if ((!list || list.length === 0) && navigator.onLine) {
          list = await getPOIsOffline(lat, lon, 50000)
        }
        if (!stopped) setNearby(list)
      } catch (e) {
        if (!stopped) setNearby([])
      }
    }
    
    readSettings().then(f => {
      if (f.enabled) {
        loadWith(f.lat, f.lon)
      } else {
        try {
          navigator.geolocation.getCurrentPosition(p => loadWith(p.coords.latitude, p.coords.longitude), () => {
            loadWith(-33.9249, 18.4241)
          }, { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 })
        } catch {
          loadWith(-33.9249, 18.4241)
        }
      }
    })
    const onDevLoc = async () => {
      const f = await readSettings()
      if (f.enabled) loadWith(f.lat, f.lon)
    }
    
    window.addEventListener('devloc-changed', onDevLoc)
    const id = navigator.geolocation.watchPosition(async p => {
      const f = await readSettings()
      const lat = f.enabled ? f.lat : p.coords.latitude
      const lon = f.enabled ? f.lon : p.coords.longitude
      loadWith(lat, lon)
    }, async () => {
      const f = await readSettings()
      if (f.enabled) loadWith(f.lat, f.lon)
      else loadWith(-33.9249, 18.4241)
    }, { enableHighAccuracy: true, maximumAge: 10000, timeout: 20000 })
    
    return () => { stopped = true; window.removeEventListener('devloc-changed', onDevLoc); navigator.geolocation.clearWatch(id) }
  }, [])
  
  // Default to Cape Town city centre until GPS resolves
  const initial: [number, number] = pos ? [pos.lat, pos.lon] : [-33.9249, 18.4241]
  
  // Recentre the map whenever the user's own position changes (not tied to POI list)
  function Recenter({ target }: { target?: { lat: number; lon: number } }) {
    const map = useMap()
    useEffect(() => {
      if (target) map.setView([target.lat, target.lon], 16)
    }, [target, map])
    return null
  }
  
  return (
    <div className="w-full h-full bg-[#e3e5e8]">
      <MapContainer center={initial} zoom={15} style={{ height: '100%', width: '100%' }} zoomControl={false}>
        <TileLayer 
          url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png" 
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
        />
        <Recenter target={pos || undefined} />
        {pos && <Marker position={[pos.lat, pos.lon]} icon={userIcon}><Popup className="font-body font-bold text-on-surface">You</Popup></Marker>}
        {nearby.map((it) => (
          <Marker key={it.id} position={[it.lat, it.lon]} icon={poiIcon}>
            <Popup className="font-body text-on-surface p-0 custom-popup">
              <div className="flex flex-col gap-2 min-w-[150px]">
                <h3 className="font-headline font-bold text-base leading-tight mt-1">{it.name}</h3>
                <div className="flex gap-2">
                  <a 
                    href={`https://www.google.com/maps/dir/?api=1&destination=${it.lat},${it.lon}`} 
                    target="_blank" rel="noreferrer"
                    className="flex-1 bg-primary text-on-primary text-xs font-bold py-2 px-2 rounded-lg flex items-center justify-center gap-1 hover:brightness-110 transition-all mt-1"
                  >
                    <span className="material-symbols-outlined text-[16px]">directions</span>
                    Nav
                  </a>
                  <button 
                    onClick={async () => {
                      let d = await getPOIContent(it.id, lang)
                      if (navigator.onLine && (!d || (d.summary?.lang && d.summary.lang !== lang))) {
                          const fetched = await fetchPOI(it.id, lang)
                          if (fetched) {
                            d = fetched
                            await savePOIContent(it.id, lang, d)
                          }
                      }
                      setSelected(d || { name: it.name })
                      setOpen(true)
                    }}
                    className="flex-1 bg-secondary text-on-secondary text-xs font-bold py-2 px-2 rounded-lg flex items-center justify-center gap-1 hover:brightness-110 transition-all mt-1"
                  >
                    <span className="material-symbols-outlined text-[16px]">info</span>
                    Details
                  </button>
                </div>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>

      {/* Map Controls */}
      <div className="absolute right-6 top-6 flex flex-col gap-3 z-[1000]">
        <button tabIndex={-1} className="bg-white/80 backdrop-blur-xl p-3 rounded-xl shadow-xl border border-white/40 text-primary hover:bg-white transition-all flex items-center justify-center">
          <span className="material-symbols-outlined">layers</span>
        </button>
      </div>
      
      <POIDetails data={selected} open={open} onClose={() => setOpen(false)} lang={lang} />
    </div>
  )
}
